import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  Divider,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import {
  apiCallDaysList,
  apiCallVenueSaveSlots,
  apiCallVenueGetSlots,
} from "../../../../Core/Apicall";
import { useSelector } from "react-redux";
import { RootState } from "../../../../Redux/Store";
import moment, { Moment } from "moment-timezone";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

interface TimeSlot {
  id: string;
  venueOpenTime: string;
  venueCloseTime: string;
  venueType: any;
}

const VENUE_TYPES = [
  { label: "Restaurant", value: "Restaurant" },
  { label: "Hotel", value: "Hotel" },
  { label: "Bar", value: "Bar" },
  { label: "Pub", value: "Pub" },
  { label: "Shop", value: "Shop" },
];

interface DayData {
  isHoliday: boolean;
  slots: TimeSlot[];
}

interface DayFromAPI {
  _id: string;
  dayName: string;
}

interface OperationalHoursProps {
  venueId: string;
}

const getDefaultDay = (): DayData => ({ isHoliday: false, slots: [] });

const genId = () =>
  `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;

const makeSlot = (open = "09:00", close = "17:00"): TimeSlot => ({
  id: genId(),
  venueOpenTime: open,
  venueCloseTime: close,
  venueType: null,
});

/** Helpers to work with HH:mm using moment */
const parseTime = (t: string): Moment => moment(t, "HH:mm");
const formatTime = (m: Moment): string => m.format("HH:mm");
const clampToNextMinute = (m: Moment): Moment => m.clone().startOf("minute");

const OperationalHours: React.FC<OperationalHoursProps> = ({ venueId }) => {
  const [apiDays, setApiDays] = useState<DayFromAPI[]>([]);
  const [days, setDays] = useState<Record<string, DayData>>({});
  const [activeTab, setActiveTab] = useState(0);
  const userdata: any = useSelector((state: RootState) => state.global.token);
  const [timeZone, setTimeZone] = useState<string>("");

  useEffect(() => {
    const fetchDays = async () => {
      try {
        const response: any = await apiCallDaysList({});
        const activeDays: DayFromAPI[] = (response.data || []).filter(
          (d: any) => d.isActive && !d.isDelete
        );
        setApiDays(activeDays);

        const obj: Record<string, DayData> = {};
        activeDays.forEach((d: any) => (obj[d._id] = getDefaultDay()));
        setDays(obj);
        fetchExistingSlots(activeDays, obj);
      } catch (err) {
        console.error("Error fetching days list:", err);
      }
    };

    fetchDays();
  }, [venueId]);

  const fetchExistingSlots = async (
    activeDays: DayFromAPI[],
    defaultDaysObj: Record<string, DayData>
  ) => {
    try {
      const res: any = await apiCallVenueGetSlots({ venueId });

      const saved = res?.data?.slots || {};
      setTimeZone(res?.data?.timeZone || "");

      const updatedDays: Record<string, DayData> = { ...defaultDaysObj };

      Object.entries(saved).forEach(([_, value]: any) => {
        if (value.daysIds && updatedDays[value.daysIds]) {
          updatedDays[value.daysIds] = {
            isHoliday: !!value.isHoliday,
            slots: (value.slots || []).map((slot: any) => ({
              id: genId(),
              venueOpenTime: moment(slot.venueOpenTime)
                .tz(res?.data.timeZone || moment.tz.guess())
                .format("HH:mm"),
              venueCloseTime: moment(slot.venueCloseTime)
                .tz(res?.data.timeZone || moment.tz.guess())
                .format("HH:mm"),
              venueType: slot.venueType || null,
            })),
          };
        }
      });

      setDays(updatedDays);
    } catch (err) {
      console.error("Error fetching slots:", err);
    }
  };

  const toggleDay = (key: string) => {
    setDays((prev) => {
      const updated = { ...prev };
      updated[key] = { ...updated[key], isHoliday: !updated[key].isHoliday };

      // If toggled ON (becomes available) and no slots exist, add a default slot
      if (!updated[key].isHoliday && updated[key].slots.length === 0) {
        updated[key].slots = [makeSlot()];
      }

      return updated;
    });
  };

  const addSlot = (key: string) => {
    setDays((prev) => {
      const prevSlots = prev[key]?.slots || [];
      let newSlot = makeSlot();

      if (prevSlots.length > 0) {
        const last = prevSlots[prevSlots.length - 1];
        const suggestedOpen = parseTime(last.venueCloseTime);
        const suggestedClose = clampToNextMinute(
          suggestedOpen.clone().add(1, "hour")
        );
        newSlot = makeSlot(
          formatTime(suggestedOpen),
          formatTime(suggestedClose)
        );
      }

      return {
        ...prev,
        [key]: {
          ...prev[key],
          slots: [...prev[key].slots, newSlot],
        },
      };
    });
  };

  const updateSlot = (
    key: string,
    slotId: string,
    field: "venueOpenTime" | "venueCloseTime",
    value: string
  ) => {
    setDays((prev) => {
      const allSlots = prev[key].slots.slice();
      const idx = allSlots.findIndex((s) => s.id === slotId);
      if (idx === -1) return prev;

      const slot = { ...allSlots[idx] };

      if (field === "venueOpenTime") {
        if (idx > 0) {
          const prevClose = parseTime(allSlots[idx - 1].venueCloseTime);
          const newOpen = parseTime(value);
          if (newOpen.isBefore(prevClose) || newOpen.isSame(prevClose)) {
            slot.venueOpenTime = formatTime(prevClose);
            // toast.info("Slot start adjusted to previous slot's closing time.");
          } else {
            slot.venueOpenTime = formatTime(newOpen);
          }
        } else {
          slot.venueOpenTime = value;
        }

        const newOpenMoment = parseTime(slot.venueOpenTime);
        const closeMoment = parseTime(slot.venueCloseTime);
        if (!newOpenMoment.isBefore(closeMoment)) {
          const newClose = clampToNextMinute(
            newOpenMoment.clone().add(1, "hour")
          );
          slot.venueCloseTime = formatTime(newClose);
          // toast.info("Slot end adjusted to be after the start time.");
        }

        allSlots[idx] = slot;
      } else {
        const newClose = parseTime(value);
        const openMoment = parseTime(slot.venueOpenTime);
        if (!openMoment.isBefore(newClose)) {
          const suggestedClose = clampToNextMinute(
            openMoment.clone().add(1, "hour")
          );
          slot.venueCloseTime = formatTime(suggestedClose);
          // toast.info(
          //   "Closing time must be after opening time — adjusted automatically."
          // );
        } else {
          slot.venueCloseTime = formatTime(newClose);
        }

        allSlots[idx] = slot;

        if (idx + 1 < allSlots.length) {
          const next = { ...allSlots[idx + 1] };
          const nextOpenMoment = parseTime(next.venueOpenTime);
          const updatedCloseMoment = parseTime(allSlots[idx].venueCloseTime);
          if (!nextOpenMoment.isSameOrAfter(updatedCloseMoment)) {
            next.venueOpenTime = formatTime(updatedCloseMoment);
            const nextCloseMoment = parseTime(next.venueCloseTime);
            if (!nextCloseMoment.isAfter(updatedCloseMoment)) {
              next.venueCloseTime = formatTime(
                clampToNextMinute(updatedCloseMoment.clone().add(1, "hour"))
              );
            }
            allSlots[idx + 1] = next;
            // toast.info(
            //   "Next slot adjusted to start after this slot's new closing time."
            // );
          }
        }
      }

      return { ...prev, [key]: { ...prev[key], slots: allSlots } };
    });
  };

  const removeSlot = (key: string, slotId: string) => {
    setDays((prev) => {
      const updatedSlots = prev[key].slots.filter((s) => s.id !== slotId);
      return { ...prev, [key]: { ...prev[key], slots: updatedSlots } };
    });
  };

  const handleSave = async () => {
    try {
      for (const [dayId, dayData] of Object.entries(days)) {
        const currentDay =
          apiDays.find((d) => d._id === dayId)?.dayName || "This day";

        if (!dayData.isHoliday) {
          if (dayData.slots.length === 0) {
            toast.error(`Please add at least one slot for ${currentDay}`);
            return;
          }

          for (let i = 0; i < dayData.slots.length; i++) {
            const slot = dayData.slots[i];
            const open = slot.venueOpenTime;
            const close = slot.venueCloseTime;

            if (!open || !close) {
              toast.error(`Opening & Closing time required for ${currentDay}`);
              return;
            }

            if (!parseTime(open).isBefore(parseTime(close))) {
              toast.error(
                `Invalid slot in ${currentDay} — Opening must be before Closing`
              );
              return;
            }

            if (i + 1 < dayData.slots.length) {
              const next = dayData.slots[i + 1];
              if (
                !parseTime(next.venueOpenTime).isSameOrAfter(parseTime(close))
              ) {
                toast.error(
                  `Slots must not overlap in ${currentDay}. Please adjust.`
                );
                return;
              }
            }
          }
        }
      }

      const slotsPayload = Object.entries(days).map(([daysId, dayData]) => {
        const parsedSlots = dayData.slots.map((slot) => ({
          venueOpenTime: moment
            .tz(slot.venueOpenTime, "HH:mm", timeZone || moment.tz.guess())
            .utc()
            .toDate(),
          venueCloseTime: moment
            .tz(slot.venueCloseTime, "HH:mm", timeZone || moment.tz.guess())
            .utc()
            .toDate(),
          venueType: slot.venueType,
        }));
        return {
          daysId,
          isHoliday: dayData.isHoliday,
          slots: parsedSlots,
        };
      });
      const payload = {
        venueId,
        userId: userdata?.data?.userId,
        slots: slotsPayload,
      };
      await apiCallVenueSaveSlots(payload);
      toast.success("Slots saved successfully");
    } catch (err) {
      console.error("Error saving slots:", err);
      toast.error("Failed to save slots");
    }
  };

  const updateSlotType = (dayId: string, slotId: string, venueType: string) => {
    setDays((prev) => {
      const slots = prev[dayId].slots.map((s) =>
        s.id === slotId ? { ...s, venueType } : s
      );
      return { ...prev, [dayId]: { ...prev[dayId], slots } };
    });
  };

  const activeDayId = apiDays[activeTab]?._id;
  const activeDayLabel = apiDays[activeTab]?.dayName;

  return (
    <Box p={3}>
      <Paper sx={{ borderRadius: 3, p: 3 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          Operational Hours
        </Typography>

        <Box display="flex" gap={3}>
          <Box minWidth={160} borderRight={1} borderColor="#e0e0e0">
            <Tabs
              orientation="vertical"
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
            >
              {apiDays.map((d) => (
                <Tab
                  key={d._id}
                  sx={{ "&.Mui-selected": { color: "black" } }}
                  label={d.dayName}
                />
              ))}
            </Tabs>
          </Box>

          <Box flex={1} pl={2}>
            {activeDayId && days[activeDayId] && (
              <>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography fontWeight={600}>{activeDayLabel}</Typography>
                  <Button
                    variant="contained"
                    size="small"
                    color={
                      !days[activeDayId]?.isHoliday ? "success" : "inherit"
                    }
                    onClick={() => toggleDay(activeDayId)}
                  >
                    {!days[activeDayId]?.isHoliday ? "On" : "Off"}
                  </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                {!days[activeDayId]?.isHoliday && (
                  <>
                    {days[activeDayId].slots.map((slot) => (
                      <Box
                        key={slot.id}
                        display="flex"
                        alignItems="center"
                        gap={2}
                        mb={2}
                      >
                        <TextField
                          label="Opening Time"
                          type="time"
                          value={slot.venueOpenTime}
                          onChange={(e) =>
                            updateSlot(
                              activeDayId,
                              slot.id,
                              "venueOpenTime",
                              e.target.value
                            )
                          }
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />

                        <Typography>to</Typography>

                        <TextField
                          label="Closing Time"
                          type="time"
                          value={slot.venueCloseTime}
                          onChange={(e) =>
                            updateSlot(
                              activeDayId,
                              slot.id,
                              "venueCloseTime",
                              e.target.value
                            )
                          }
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />

                        <FormControl size="small" sx={{ minWidth: 160 }}>
                          <InputLabel id={`venue-type-label-${slot.id}`} shrink>
                            Venue Type
                          </InputLabel>

                          <Select
                            value={slot.venueType || null}
                            onChange={(e) =>
                              updateSlotType(
                                activeDayId,
                                slot.id,
                                e.target.value === "" ? null : e.target.value
                              )
                            }
                            displayEmpty
                            label="Venue Type"
                            renderValue={(selected) =>
                              selected
                                ? VENUE_TYPES.find((v) => v.value === selected)
                                    ?.label
                                : "Select Venue Type"
                            }
                          >
                            <MenuItem value="">Select Venue Type</MenuItem>

                            {VENUE_TYPES.map((item) => (
                              <MenuItem key={item.value} value={item.value}>
                                {item.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          onClick={() => removeSlot(activeDayId, slot.id)}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}

                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => addSlot(activeDayId)}
                    >
                      + Add Slot
                    </Button>
                  </>
                )}
              </>
            )}
          </Box>
        </Box>

        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            style={{ backgroundColor: "black", color: "white" }}
          >
            Save
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default OperationalHours;
